/**
 * Resolve product permission IDs → included/excluded sub-module labels.
 * Mirrors admin product-permissions logic, but subscription UI shows sub-modules only
 * (no component-level children).
 */

export type ActivePermission = {
  id: string;
  identifier: string;
  name: string;
};

export type SolutionSubModule = {
  id: string;
  identifier: string;
  label: string;
  /** IDs and identifiers that count as "this module is on the product" (self + components). */
  memberKeys: string[];
};

const READ_SUFFIX = '-read';

/** Normalize solution display → slug used in identifiers (`EDR` / `… (EDR)` → `edr`). */
export const toSolutionSlug = (solution: string): string => {
  const trimmed = String(solution || '').trim();
  if (!trimmed) return '';
  const paren = trimmed.match(/\(([A-Za-z0-9]+)\)\s*$/);
  if (paren?.[1]) return paren[1].toLowerCase();
  return trimmed
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
};

const humanizeSegment = (segment: string): string =>
  segment
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const permissionBody = (identifier: string): string => {
  if (identifier.endsWith(READ_SUFFIX)) {
    return identifier.slice(0, -READ_SUFFIX.length);
  }
  return identifier;
};

const normalizePermission = (raw: any): ActivePermission | null => {
  const id = String(raw?.id || raw?._id || '').trim();
  const identifier = String(raw?.identifier || '').trim().toLowerCase();
  if (!id || !identifier) return null;
  const name = String(raw?.name || '').trim();
  return { id, identifier, name };
};

/**
 * Active permissions for a solution: `client_{slug}_…-read`, excluding SOC overview.
 */
export const filterSolutionReadPermissions = (
  permissions: ActivePermission[],
  solutionSlug: string,
): ActivePermission[] => {
  const slug = solutionSlug.trim().toLowerCase();
  if (!slug) return [];
  const prefix = `client_${slug}_`;
  const socExact = `client_${slug}_soc${READ_SUFFIX}`;

  return permissions.filter((permission) => {
    const { identifier } = permission;
    if (!identifier.startsWith(prefix) || !identifier.endsWith(READ_SUFFIX)) {
      return false;
    }
    if (identifier === socExact || identifier.startsWith(`client_${slug}_soc_`)) {
      return false;
    }
    return true;
  });
};

/** True when another permission is a proper prefix parent (component under a sub-module). */
const isComponentPermission = (
  permission: ActivePermission,
  catalog: ActivePermission[],
): boolean => {
  const body = permissionBody(permission.identifier);
  return catalog.some((other) => {
    if (other.identifier === permission.identifier) return false;
    const otherBody = permissionBody(other.identifier);
    return body.startsWith(`${otherBody}_`);
  });
};

const moduleLabel = (permission: ActivePermission, solutionSlug: string): string => {
  if (permission.name) return permission.name;
  const prefix = `client_${solutionSlug}_`;
  const body = permissionBody(permission.identifier);
  const segment = body.startsWith(prefix) ? body.slice(prefix.length) : body;
  return humanizeSegment(segment) || permission.identifier;
};

/**
 * Top-level sub-modules for a solution (parents only; components omitted from the list).
 */
export const buildSolutionSubModules = (
  activePermissions: ActivePermission[],
  solution: string,
): SolutionSubModule[] => {
  const slug = toSolutionSlug(solution);
  if (!slug) return [];

  const catalog = filterSolutionReadPermissions(activePermissions, slug);
  if (!catalog.length) return [];

  const components = catalog.filter((permission) =>
    isComponentPermission(permission, catalog),
  );
  const parents = catalog.filter(
    (permission) => !isComponentPermission(permission, catalog),
  );

  return parents
    .map((parent) => {
      const parentBody = permissionBody(parent.identifier);
      const children = components.filter((component) =>
        permissionBody(component.identifier).startsWith(`${parentBody}_`),
      );

      const memberKeys = [
        parent.id,
        parent.identifier,
        ...children.flatMap((component) => [component.id, component.identifier]),
      ];

      return {
        id: parent.id,
        identifier: parent.identifier,
        label: moduleLabel(parent, slug),
        memberKeys,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
};

export const resolveIncludedExcludedModules = (
  activePermissions: ActivePermission[],
  solution: string | undefined,
  permissionIds: string[],
): { included: string[]; excluded: string[] } => {
  const modules = buildSolutionSubModules(activePermissions, solution || '');
  if (!modules.length) {
    return { included: [], excluded: [] };
  }

  const onProduct = new Set(
    (permissionIds || [])
      .map((id) => String(id ?? '').trim())
      .filter(Boolean)
      .map((id) => id.toLowerCase()),
  );

  // Product stores Mongo ids; match case-sensitively for ids and lowercased for identifiers.
  const onProductRaw = new Set(
    (permissionIds || []).map((id) => String(id ?? '').trim()).filter(Boolean),
  );

  const included: string[] = [];
  const excluded: string[] = [];

  modules.forEach((module) => {
    const isIncluded = module.memberKeys.some(
      (key) => onProductRaw.has(key) || onProduct.has(key.toLowerCase()),
    );
    if (isIncluded) included.push(module.label);
    else excluded.push(module.label);
  });

  return { included, excluded };
};

export const mapActivePermissions = (rows: any[]): ActivePermission[] =>
  (Array.isArray(rows) ? rows : [])
    .map(normalizePermission)
    .filter(Boolean) as ActivePermission[];
